import { Add as AddIcon } from "@mui/icons-material";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { SelectAutocompleteSingle } from "../../../../../common/components/functional/SelectAutocomplete/SelectAutocompleteSingle";
import { EventPayload } from "../../../../../common/types/Event";
import { AssignmentById } from "../../../../talents/talentAPI";
import {
  fetchAssignmentSupplementPriceScheduleByCode,
  postChangeAssignmentSupplementPriceSchedule,
} from "../../../../talents/talentsSlice";
import {
  AssignmentSupplementPriceOverridesChangedV2Payload,
  priceListUnits,
  PriceValueType,
  TesPricingOverrideOptions,
} from "../../../../tesPricingOverride/tesPricingOverrideAPI";
import AssignmentSupplementPriceOverridesRow, {
  SupplementScheduleFormValues,
} from "./AssignmentSupplementPriceOverridesRow";
import { adjustForMultiplier } from "../helpers";
import { AssignmentSupplementPriceOverridesRowData } from "./assignmentSupplementsPriceOverridesMapper";

type FormValues = SupplementScheduleFormValues;

interface Props {
  open: boolean;
  mode: "add" | "edit";
  selectedRow: AssignmentSupplementPriceOverridesRowData | null;
  supplementLabel?: string;
  assignment: AssignmentById;
  existingCodes: string[];
  tesPricingOverrideOptions: TesPricingOverrideOptions;
  onClose: () => void;
  onSaved: (
    events: EventPayload<AssignmentSupplementPriceOverridesChangedV2Payload>[],
  ) => void;
}

const AssignmentSupplementsPriceOverridesDialog = ({
  open,
  mode,
  selectedRow,
  assignment,
  existingCodes,
  tesPricingOverrideOptions,
  onClose,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const [unit, setUnit] = useState<PriceValueType>("FIXED_PRICE");
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<FormValues>({
    defaultValues: { code: "", rows: [] },
    mode: "onChange",
  });

  const { control, reset, watch, handleSubmit, setValue, formState } = methods;

  const { fields, append, remove } = useFieldArray({ name: "rows", control });

  const selectedCode = watch("code");
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!open) {
      reset({ code: "", rows: [] });
      setUnit("FIXED_PRICE");
      return;
    }

    if (isEditMode && selectedRow) {
      setIsLoading(true);
      dispatch(
        fetchAssignmentSupplementPriceScheduleByCode({
          assignmentId: assignment.assignmentId,
          code: selectedRow.code,
        }),
      )
        .then((action) => {
          if (isFulfilled(action) && action.payload) {
            const { unit: fetchedUnit, schedule } = action.payload;
            setUnit(fetchedUnit);
            reset({
              code: selectedRow.code,
              rows: schedule.map((pair) => ({
                date: new Date(pair.date),
                value: adjustForMultiplier(pair.value, fetchedUnit),
              })),
            });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open, isEditMode, selectedRow, assignment.assignmentId, dispatch, reset]);

  useEffect(() => {
    if (!isEditMode && selectedCode) {
      setValue(
        "rows",
        [
          {
            date: new Date(assignment.details.start),
            value: tesPricingOverrideOptions[selectedCode]?.unitValue || "",
          },
        ],
        { shouldDirty: true },
      );
      setUnit(
        (tesPricingOverrideOptions[selectedCode]?.unit as PriceValueType) ||
          "FIXED_PRICE",
      );
    }
    // eslint-disable-next-line
  }, [selectedCode]);

  const supplementOptions = Object.keys(tesPricingOverrideOptions)
    .map((key) => ({
      id: key,
      name: translatedSupplementNames?.[key] || key,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "fi"))
    .filter((s) => !existingCodes.includes(s.id));

  const activeCode = isEditMode ? selectedRow?.code : selectedCode;

  const isSaveDisabled =
    isLoading ||
    !formState.isValid ||
    !formState.isDirty ||
    (!isEditMode && fields.length === 0);

  const onSubmit = async (data: FormValues) => {
    if (!activeCode) return;

    const values = data.rows
      .filter((row) => row.date && row.value)
      .map((row) => ({
        date: format(row.date!, "yyyy-MM-dd"),
        value:
          unit === "SALARY_MULTIPLIER"
            ? (Number(row.value) / 100).toString()
            : row.value,
      }))
      // Sort is required by the backend
      .sort((a, b) => a.date.localeCompare(b.date));

    setIsLoading(true);
    const action = await dispatch(
      postChangeAssignmentSupplementPriceSchedule({
        assignmentId: assignment.assignmentId,
        version: assignment.details.version,
        code: activeCode,
        values,
      }),
    );
    setIsLoading(false);

    if (isFulfilled(action)) {
      onSaved(action.payload);
      onClose();
    }
  };

  const title = !isEditMode
    ? t("tesPricingOverride:editTesPricingOverride")
    : t("tesPricingOverride:editTitle");

  const unitSymbol = priceListUnits[unit] ?? "";
  const showEmptyState = fields.length === 0 && !isLoading && isEditMode;

  const assignmentStartDate = startOfDay(new Date(assignment.details.start));
  // end is exclusive (first moment after the assignment), subtract 1ms to get the last valid day
  const assignmentEndDate = assignment.details.end
    ? startOfDay(new Date(assignment.details.end - 1))
    : undefined;

  return (
    <DialogComponent open={open} onClose={onClose} sx={{ width: "464px" }}>
      <DialogContentWrapper
        margin
        title={title}
        onCancel={onClose}
        onSave={handleSubmit(onSubmit)}
        saveDisabled={isSaveDisabled}
      >
        <FormProvider {...methods}>
          <Grid item xs={12} sx={{ position: "relative", minHeight: "80px" }}>
            <OverlaySpinner isLoading={isLoading} />

            {!isEditMode && (
              <Box sx={{ mb: 2 }}>
                <Controller
                  name="code"
                  control={control}
                  rules={{ required: mode === "add" }}
                  render={({ field }) => (
                    <SelectAutocompleteSingle
                      value={
                        supplementOptions.find(
                          (opt) => opt.id === field.value,
                        ) || null
                      }
                      options={supplementOptions}
                      onChange={(value) =>
                        field.onChange(value ? value.id : "")
                      }
                      placeholder={t("tesPricingOverride:selectSupplement")}
                    />
                  )}
                />
              </Box>
            )}

            {showEmptyState && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("tesPricingOverride:noScheduleRows")}
              </Typography>
            )}

            {fields.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("tesPricingOverride:date")}</TableCell>
                    <TableCell>
                      {t("tesPricingOverride:selectValueLabel")}
                      {unitSymbol ? ` (${unitSymbol})` : ""}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <AssignmentSupplementPriceOverridesRow
                      key={field.id}
                      index={index}
                      minDate={assignmentStartDate}
                      maxDate={assignmentEndDate}
                      onRemove={() => remove(index)}
                    />
                  ))}
                </TableBody>
              </Table>
            )}

            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
              <CustomButton
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => append({ date: null, value: "" })}
                disabled={isLoading || (!isEditMode && !selectedCode)}
                sx={{ height: "32px", borderRadius: "20px" }}
              >
                {t("tesPricingOverride:addRow")}
              </CustomButton>
            </Box>
          </Grid>
        </FormProvider>
      </DialogContentWrapper>
    </DialogComponent>
  );
};

export default AssignmentSupplementsPriceOverridesDialog;
